import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Grid,
    CircularProgress,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Button,
    Alert,
    Snackbar,
    Chip,
    Box,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import { WebOutlined, Save, Refresh, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { baseUrl } from '../../../../config/constants';
import { userStore } from '../../../../app/stores/User';
import { useStoreConfig } from '../../../../contexts/StoreConfigContext';

interface AvailableScreen {
    screenKey: string;
    displayName: string;
    description?: string;
    category?: string;
    defaultNavBar: boolean;
    defaultDrawer: boolean;
    defaultSortOrder?: number;
}

interface ScreenConfig {
    id?: string;
    name: string;
    displayName: string;
    screenKey: string;
    category?: string;
    showInNavBar: boolean;
    showInDrawer: boolean;
    sortOrder: number;
    isActive: boolean;
    icon?: string;
    route?: string;
}

const CustomScreens: React.FC = () => {
    const theme = useTheme();
    const { user } = userStore((state) => state);
    const { config } = useStoreConfig();
    const token = user.session?.access_token;
    
    // Get current store from context
    const currentStoreId = config.storeId;
    const currentStoreName = config.storeName;

    const [availableScreens, setAvailableScreens] = useState<AvailableScreen[]>([]);
    const [screenConfigs, setScreenConfigs] = useState<ScreenConfig[]>([]);
    const [initialScreenConfigs, setInitialScreenConfigs] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Track if there are unsaved changes
    const isDirty = JSON.stringify(screenConfigs) !== initialScreenConfigs;

    // Fetch available screen components
    const fetchAvailableScreens = useCallback(async () => {
        try {
            const response = await fetch(`${baseUrl}/platform-admin/screens/available-screens`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) throw new Error('Failed to fetch available screens');
            const data = await response.json();
            setAvailableScreens(data.screens || []);
        } catch (err) {
            console.error('Error fetching available screens:', err);
            setError('Failed to load available screens');
        }
    }, [token]);

    // Fetch screen configs for current store
    const fetchStoreScreens = useCallback(async () => {
        if (!currentStoreId) return;
        
        try {
            setIsLoading(true);
            const response = await fetch(`${baseUrl}/platform-admin/screens/store/${currentStoreId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) throw new Error('Failed to fetch store screens');
            const data = await response.json();
            const screens = data.screens || [];
            setScreenConfigs(screens);
            setInitialScreenConfigs(JSON.stringify(screens));
        } catch (err) {
            console.error('Error fetching store screens:', err);
            setError('Failed to load store screens');
        } finally {
            setIsLoading(false);
        }
    }, [token, currentStoreId]);

    // Initialize data when component mounts
    useEffect(() => {
        const loadData = async () => {
            if (!currentStoreId) return;
            
            setIsLoading(true);
            await Promise.all([fetchAvailableScreens(), fetchStoreScreens()]);
            setIsLoading(false);
        };
        loadData();
    }, [currentStoreId, fetchAvailableScreens, fetchStoreScreens]);

    // Get merged screen list (available + configured)
    const getMergedScreens = (): (AvailableScreen & Partial<ScreenConfig>)[] => {
        return availableScreens.map(available => {
            const config = screenConfigs.find(c => c.screenKey === available.screenKey);
            // Use defaults from available screen if no config exists
            const isCore = available.category === 'Core';
            return {
                ...available,
                ...config,
                // Preserve category from available screen definition (source of truth)
                category: available.category,
                showInNavBar: config?.showInNavBar ?? (isCore ? available.defaultNavBar : false),
                showInDrawer: config?.showInDrawer ?? (isCore ? available.defaultDrawer : false),
                sortOrder: config?.sortOrder ?? (available.defaultSortOrder ?? 1),
                isActive: config?.isActive ?? isCore, // Core screens default to active
            };
        });
    };

    // Handle screen config change
    const handleScreenChange = (screenKey: string, field: string, value: boolean | number | string) => {
        // For sortOrder changes, use special reordering logic
        if (field === 'sortOrder') {
            handleSortOrderChange(screenKey, value as number);
            return;
        }
        
        setScreenConfigs(prev => {
            const existingIndex = prev.findIndex(s => s.screenKey === screenKey);
            const available = availableScreens.find(a => a.screenKey === screenKey);
            
            if (existingIndex >= 0) {
                // Update existing
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    [field]: value,
                };
                return updated;
            } else {
                // Create new
                return [...prev, {
                    name: screenKey.toLowerCase().replace(/_/g, '-'),
                    displayName: available?.displayName || screenKey,
                    screenKey,
                    showInNavBar: field === 'showInNavBar' ? value as boolean : false,
                    showInDrawer: field === 'showInDrawer' ? value as boolean : false,
                    sortOrder: 0,
                    isActive: field === 'isActive' ? value as boolean : true,
                }];
            }
        });
    };

    // Handle sort order change with automatic reordering
    const handleSortOrderChange = (screenKey: string, newPosition: number) => {
        // Get all current screens with their positions
        const currentScreens = getMergedScreens().filter(s => s.isActive);
        
        // Clamp position to valid range
        const maxPosition = currentScreens.length;
        const clampedPosition = Math.max(1, Math.min(newPosition, maxPosition));
        
        // Find the screen being moved
        const movingScreen = currentScreens.find(s => s.screenKey === screenKey);
        if (!movingScreen) return;
        
        const oldPosition = movingScreen.sortOrder ?? 99;
        
        // If position didn't change, do nothing
        if (oldPosition === clampedPosition) return;
        
        // Calculate new sort orders for all screens
        const newOrders: { screenKey: string; sortOrder: number }[] = [];
        
        currentScreens.forEach(screen => {
            if (screen.screenKey === screenKey) {
                // This is the screen being moved
                newOrders.push({ screenKey: screen.screenKey, sortOrder: clampedPosition });
            } else {
                // Shift other screens
                let currentOrder = screen.sortOrder ?? 99;
                
                if (oldPosition < clampedPosition) {
                    // Moving down: shift screens between old and new position up
                    if (currentOrder > oldPosition && currentOrder <= clampedPosition) {
                        currentOrder--;
                    }
                } else {
                    // Moving up: shift screens between new and old position down
                    if (currentOrder >= clampedPosition && currentOrder < oldPosition) {
                        currentOrder++;
                    }
                }
                
                newOrders.push({ screenKey: screen.screenKey, sortOrder: currentOrder });
            }
        });
        
        // Apply the new orders to screenConfigs
        setScreenConfigs(prev => {
            const updated = [...prev];
            
            newOrders.forEach(({ screenKey: key, sortOrder }) => {
                const existingIndex = updated.findIndex(s => s.screenKey === key);
                const available = availableScreens.find(a => a.screenKey === key);
                
                if (existingIndex >= 0) {
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        sortOrder,
                    };
                } else {
                    // Create new config entry
                    updated.push({
                        name: key.toLowerCase().replace(/_/g, '-'),
                        displayName: available?.displayName || key,
                        screenKey: key,
                        showInNavBar: available?.defaultNavBar ?? false,
                        showInDrawer: available?.defaultDrawer ?? false,
                        sortOrder,
                        isActive: true,
                    });
                }
            });
            
            return updated;
        });
    };

    // Save configurations
    const handleSave = async () => {
        if (!currentStoreId) {
            setError('Store not found');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Prepare screens to save (only include screens that are active or have config)
            const screensToSave = getMergedScreens()
                .filter(s => s.isActive || s.showInNavBar || s.showInDrawer)
                .map(s => ({
                    screenKey: s.screenKey,
                    displayName: s.displayName,
                    category: s.category || 'Custom',
                    showInNavBar: s.showInNavBar ?? false,
                    showInDrawer: s.showInDrawer ?? false,
                    sortOrder: s.sortOrder ?? 1,
                    isActive: s.isActive ?? false,
                }));
            
            const response = await fetch(`${baseUrl}/platform-admin/screens/bulk`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    store_id: currentStoreId,
                    screens: screensToSave,
                }),
            });

            if (!response.ok) throw new Error('Failed to save screen configurations');
            
            const result = await response.json();
            setSuccess(`Saved! Created: ${result.created}, Updated: ${result.updated}`);
            
            // Refresh the data
            await fetchStoreScreens();
        } catch (err) {
            console.error('Error saving screens:', err);
            setError('Failed to save screen configurations');
        } finally {
            setIsSaving(false);
        }
    };

    // Sort screens by sortOrder (this reflects the actual navigation order)
    const mergedScreens = getMergedScreens().sort((a, b) => {
        return (a.sortOrder ?? 99) - (b.sortOrder ?? 99);
    });

    if (!currentStoreId) {
        return (
            <Container component='main' maxWidth='xl' sx={{ paddingX: { xs: 0, md: 2 } }}>
                <Alert severity='error'>
                    Store not found. Please refresh the page.
                </Alert>
            </Container>
        );
    }

    return (
        <Container
            component='main'
            maxWidth='xl'
            sx={{
                height: '100%',
                paddingX: { xs: 0, md: 2 },
            }}
        >
            <PortalScreenHeader
                title='Custom Screens'
                subtitle={`Manage custom screens for ${currentStoreName || 'this store'}`}
                icon={<WebOutlined />}
                actions={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title='Refresh'>
                            <span>
                                <IconButton
                                    onClick={fetchStoreScreens}
                                    disabled={isLoading}
                                    size='small'
                                >
                                    <Refresh fontSize='small' />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            variant='contained'
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={isSaving || !isDirty}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                }
            />

            {isLoading ? (
                <Grid container justifyContent='center' mt={10}>
                    <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
                </Grid>
            ) : (
                <>
                    {/* No custom screens available */}
                    {mergedScreens.length === 0 ? (
                        <Alert severity='info' sx={{ marginBottom: 4 }}>
                            <Typography variant='body1' fontWeight={500}>
                                No custom screens available yet
                            </Typography>
                            <Typography variant='body2' sx={{ marginTop: 1 }}>
                                To add a custom screen:
                            </Typography>
                            <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                                <li>Create the screen component in <code>screens/Main/Custom/</code></li>
                                <li>Add to <code>ScreenComponent</code> enum</li>
                                <li>Register in <code>useScreenComponent.ts</code> hook</li>
                                <li>Add to the API&apos;s available screens list</li>
                                <li>Come back here to enable it for this store</li>
                            </ol>
                        </Alert>
                    ) : (
                                <>
                                    {/* Info Banner */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            marginBottom: 3,
                                            padding: 2,
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(33, 150, 243, 0.08)'
                                                : 'rgba(33, 150, 243, 0.04)',
                                            border: `1px solid ${theme.palette.mode === 'dark'
                                                ? 'rgba(33, 150, 243, 0.2)'
                                                : 'rgba(33, 150, 243, 0.15)'}`,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                gap: { xs: 3, sm: 5 },
                                                flexWrap: 'wrap',
                                            }}>
                                                <Box sx={{ textAlign: 'left' }}>
                                                    <Typography variant='caption' color='primary' fontWeight={600} display='block'>Active</Typography>
                                                    <Typography variant='body2' color='text.secondary'>Enable for {currentStoreName || 'store'}</Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'left' }}>
                                                    <Typography variant='caption' color='primary' fontWeight={600} display='block'>NavBar</Typography>
                                                    <Typography variant='body2' color='text.secondary'>Desktop navigation</Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'left' }}>
                                                    <Typography variant='caption' color='primary' fontWeight={600} display='block'>Drawer</Typography>
                                                    <Typography variant='body2' color='text.secondary'>Mobile menu</Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'left' }}>
                                                    <Typography variant='caption' color='primary' fontWeight={600} display='block'>Order</Typography>
                                                    <Typography variant='body2' color='text.secondary'>Use arrows to reorder</Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant='caption' color='text.disabled'>
                                                Changes apply after page refresh
                                            </Typography>
                                        </Box>
                                    </Paper>

                                    {/* Screen Configuration Table */}
                        <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Screen</strong></TableCell>
                                        <TableCell align='center'><strong>Type</strong></TableCell>
                                        <TableCell align='center'><strong>Active</strong></TableCell>
                                        <TableCell align='center'><strong>NavBar</strong></TableCell>
                                        <TableCell align='center'><strong>Drawer</strong></TableCell>
                                        <TableCell align='center'><strong>Order</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mergedScreens.map((screen) => (
                                        <TableRow key={screen.screenKey}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant='body1' fontWeight={500}>
                                                        {screen.displayName}
                                                    </Typography>
                                                    {screen.description && (
                                                        <Typography variant='body2' color='text.secondary'>
                                                            {screen.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Chip 
                                                    label={screen.category || 'Custom'} 
                                                    size='small' 
                                                    color={screen.category === 'Core' ? 'primary' : 'secondary'}
                                                    variant='outlined'
                                                />
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Switch
                                                    checked={screen.isActive ?? false}
                                                    onChange={(e) => handleScreenChange(
                                                        screen.screenKey, 
                                                        'isActive', 
                                                        e.target.checked
                                                    )}
                                                    color='primary'
                                                />
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Switch
                                                    checked={screen.showInNavBar ?? false}
                                                    onChange={(e) => handleScreenChange(
                                                        screen.screenKey, 
                                                        'showInNavBar', 
                                                        e.target.checked
                                                    )}
                                                    color='primary'
                                                    disabled={!screen.isActive}
                                                />
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Switch
                                                    checked={screen.showInDrawer ?? false}
                                                    onChange={(e) => handleScreenChange(
                                                        screen.screenKey, 
                                                        'showInDrawer', 
                                                        e.target.checked
                                                    )}
                                                    color='primary'
                                                    disabled={!screen.isActive}
                                                />
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => handleScreenChange(
                                                            screen.screenKey,
                                                            'sortOrder',
                                                            (screen.sortOrder ?? 1) - 1
                                                        )}
                                                        disabled={!screen.isActive || (screen.sortOrder ?? 1) <= 1}
                                                        sx={{ 
                                                            color: theme.palette.primary.main,
                                                            '&:disabled': { opacity: 0.3 }
                                                        }}
                                                    >
                                                        <KeyboardArrowUp />
                                                    </IconButton>
                                                    <Typography 
                                                        variant='body2' 
                                                        sx={{ 
                                                            minWidth: 24, 
                                                            textAlign: 'center',
                                                            fontWeight: 600,
                                                            opacity: screen.isActive ? 1 : 0.5
                                                        }}
                                                    >
                                                        {screen.sortOrder ?? 1}
                                                    </Typography>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => handleScreenChange(
                                                            screen.screenKey,
                                                            'sortOrder',
                                                            (screen.sortOrder ?? 1) + 1
                                                        )}
                                                        disabled={!screen.isActive || (screen.sortOrder ?? 1) >= mergedScreens.filter(s => s.isActive).length}
                                                        sx={{ 
                                                            color: theme.palette.primary.main,
                                                            '&:disabled': { opacity: 0.3 }
                                                        }}
                                                    >
                                                        <KeyboardArrowDown />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                                </>
                    )}
                </>
            )}

            {/* Error Snackbar */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError(null)}
            >
                <Alert severity='error' onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>

            {/* Success Snackbar */}
            <Snackbar 
                open={!!success} 
                autoHideDuration={4000} 
                onClose={() => setSuccess(null)}
            >
                <Alert severity='success' onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CustomScreens;
